import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiRequestError } from "@pustak-lms/api-client";
import type { ApiFieldErrors, CreateOrganizationInput } from "@pustak-lms/contracts";
import { Button, PageHeader, Select } from "@pustak-lms/ui";
import { SUPPORTED_COUNTRIES } from "@pustak-lms/utils";
import { platformApiClient } from "./api";

type FormValues = Required<CreateOrganizationInput>;
type FieldName = keyof FormValues;

const initialValues: FormValues = {
  name: "",
  slug: "",
  countryCode: "",
  ownerName: "",
  ownerEmail: "",
  ownerMobilePrimary: "",
  ownerMobileAlternate: "",
  organizationEmail: "",
  websiteUrl: "",
  city: "",
  stateRegion: "",
  internalNotes: ""
};

interface FieldDefinition {
  name: FieldName;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
}

const organizationFields: FieldDefinition[] = [
  { name: "slug", label: "Organization slug", required: true, placeholder: "abc", hint: "Used in your tenant hostname. Lowercase letters, numbers and hyphens." },
  { name: "name", label: "Organization name", required: true, placeholder: "Northstar Nursing Institute" },
  { name: "countryCode", label: "Country", required: true },
  { name: "organizationEmail", label: "Organization email", type: "email", placeholder: "office@example.com" },
  { name: "websiteUrl", label: "Website", type: "url", placeholder: "https://example.com" },
  { name: "city", label: "City" },
  { name: "stateRegion", label: "State or region" }
];

const ownerFields: FieldDefinition[] = [
  { name: "ownerName", label: "Owner full name", required: true, placeholder: "Avery Stone" },
  { name: "ownerEmail", label: "Owner email", type: "email", required: true, placeholder: "avery@example.com" },
  { name: "ownerMobilePrimary", label: "Primary mobile", type: "tel", required: true, placeholder: "+919876543210", hint: "Include the country calling code." },
  { name: "ownerMobileAlternate", label: "Alternate mobile", type: "tel", placeholder: "+919123456789", hint: "A second number for this same owner." }
];

const countryOptions = SUPPORTED_COUNTRIES.map(({ code, name }) => ({ value: code, label: name }));

function buildPayload(values: FormValues): CreateOrganizationInput {
  const optional = (value: string) => value.trim() || undefined;
  return {
    name: values.name.trim(),
    slug: values.slug.trim().toLowerCase(),
    countryCode: values.countryCode.trim().toUpperCase(),
    ownerName: values.ownerName.trim(),
    ownerEmail: values.ownerEmail.trim(),
    ownerMobilePrimary: values.ownerMobilePrimary.trim(),
    ...(optional(values.ownerMobileAlternate) && { ownerMobileAlternate: optional(values.ownerMobileAlternate) }),
    ...(optional(values.organizationEmail) && { organizationEmail: optional(values.organizationEmail) }),
    ...(optional(values.websiteUrl) && { websiteUrl: optional(values.websiteUrl) }),
    ...(optional(values.city) && { city: optional(values.city) }),
    ...(optional(values.stateRegion) && { stateRegion: optional(values.stateRegion) }),
    ...(optional(values.internalNotes) && { internalNotes: optional(values.internalNotes) })
  };
}

export function CreateOrganizationPage() {
  const navigate = useNavigate();
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<ApiFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function update(name: FieldName, value: string) {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: [] }));
    setSubmitError(null);
  }

  function renderField(field: FieldDefinition) {
    const message = errors[field.name]?.[0];
    const id = `organization-${field.name}`;
    const describedBy = message ? `${id}-error` : field.hint ? `${id}-hint` : undefined;
    return (
      <div className="console-field" key={field.name}>
        <label htmlFor={id}>{field.label}{field.required ? <span aria-hidden="true"> *</span> : null}</label>
        {field.name === "countryCode" ? (
          <Select
            id={id}
            name={field.name}
            value={values.countryCode}
            options={countryOptions}
            placeholder="Select a country"
            required
            disabled={submitting}
            invalid={Boolean(message)}
            describedBy={describedBy}
            onValueChange={(value) => update("countryCode", value)}
          />
        ) : (
          <input
            id={id}
            name={field.name}
            type={field.type || "text"}
            value={values[field.name]}
            placeholder={field.placeholder}
            required={field.required}
            aria-invalid={Boolean(message)}
            aria-describedby={describedBy}
            onChange={(event) => update(field.name, event.target.value)}
          />
        )}
        {message ? <small id={`${id}-error`} className="field-error">{message}</small> : null}
        {!message && field.hint ? <small id={`${id}-hint`} className="field-hint">{field.hint}</small> : null}
      </div>
    );
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    if (!values.countryCode) {
      setErrors((current) => ({ ...current, countryCode: ["Select a country"] }));
      setSubmitError(null);
      return;
    }
    setSubmitting(true);
    setErrors({});
    setSubmitError(null);

    void Promise.resolve()
      .then(() => platformApiClient().createOrganization(buildPayload(values)))
      .then((saved) => navigate("/organizations", { state: { createdName: saved.name, deliveryStatus: saved.ownerInvitation?.deliveryStatus } }))
      .catch((error: unknown) => {
        if (error instanceof ApiRequestError) {
          setErrors(error.fieldErrors);
          setSubmitError(error.message);
        } else {
          setSubmitError("Unable to save the organization. Check the connection and try again.");
        }
      })
      .finally(() => setSubmitting(false));
  }

  return (
    <>
      <PageHeader
        eyebrow="Organizations"
        title="Create organization"
        description="Record the organization and one owner's contact details. The initial owner will receive an activation invitation."
      />
      <form className="console-form" onSubmit={submit} noValidate>
        {submitError ? <div className="console-alert console-alert-error" role="alert">{submitError}</div> : null}
        <section className="panel">
          <div className="panel-header"><h2>Organization details</h2></div>
          <div className="console-field-grid">{organizationFields.map(renderField)}</div>
        </section>
        <section className="panel">
          <div className="panel-header"><h2>Owner contact</h2></div>
          <p className="console-section-description">Both mobile numbers, if provided, belong to this owner.</p>
          <div className="console-field-grid">{ownerFields.map(renderField)}</div>
        </section>
        <section className="panel">
          <div className="console-field">
            <label htmlFor="organization-internalNotes">Internal notes</label>
            <textarea
              id="organization-internalNotes"
              name="internalNotes"
              value={values.internalNotes}
              maxLength={2000}
              rows={4}
              aria-invalid={Boolean(errors.internalNotes?.[0])}
              onChange={(event) => update("internalNotes", event.target.value)}
            />
            {errors.internalNotes?.[0] ? <small className="field-error">{errors.internalNotes[0]}</small> : null}
          </div>
        </section>
        <div className="console-form-actions">
          <Button type="button" variant="secondary" onClick={() => navigate("/organizations")}>Cancel</Button>
          <Button type="submit" disabled={submitting}>{submitting ? "Creating…" : "Create organization"}</Button>
        </div>
      </form>
    </>
  );
}
